/**
 * Alert sounds — all generated via the Web Audio API (no audio files needed).
 * Each function signature: soundFn(ctx, dest)
 *   ctx  — AudioContext
 *   dest — AudioNode to connect to (typically a master GainNode)
 *
 * Internal gain values are normalized to 1.0 peak so the master GainNode
 * controls the final volume. The master gain is set by playAlertSound / loopAlertSound.
 */

// ── 1. Campanella — single soft bell tone, slow fade ─────────────────────────
function playCampanella(ctx, dest) {
  const t0 = ctx.currentTime;
  const osc  = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(dest);
  osc.type = 'sine';
  osc.frequency.value = 880;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(1.0, t0 + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 2.0);
  osc.start(t0); osc.stop(t0 + 2.1);
}

// ── 2. Carillon — three descending notes, music-box feel ─────────────────────
function playCarillon(ctx, dest) {
  [[1047, 0], [880, 0.22], [698, 0.44]].forEach(([freq, delay]) => {
    const t0   = ctx.currentTime + delay;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(1.0, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.90);
    osc.start(t0); osc.stop(t0 + 0.95);
  });
}

// ── 3. Ping — short clean digital beep ───────────────────────────────────────
function playPing(ctx, dest) {
  const t0   = ctx.currentTime;
  const osc  = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(dest);
  osc.type = 'sine';
  osc.frequency.value = 1320;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(1.0, t0 + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
  osc.start(t0); osc.stop(t0 + 0.22);
}

// ── 4. Zen — deep bowl / gong, slow fade (fundamental + harmonic) ─────────────
function playZen(ctx, dest) {
  const t0 = ctx.currentTime;
  // 180 Hz fundamental + 360 Hz harmonic; ratio 1 : 0.36 preserved
  [[180, 1.0], [360, 0.36]].forEach(([freq, vol]) => {
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(vol, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 3.5);
    osc.start(t0); osc.stop(t0 + 3.6);
  });
}

// ── 5. Fanfara — quick 4-note rising sequence, energetic ──────────────────────
function playFanfara(ctx, dest) {
  [[523, 0], [659, 0.12], [784, 0.24], [1047, 0.36]].forEach(([freq, delay]) => {
    const t0   = ctx.currentTime + delay;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = 'triangle';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(1.0, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.28);
    osc.start(t0); osc.stop(t0 + 0.32);
  });
}

// ── 6. Mattino — 5-note ascending C major arpeggio, gentle sine ───────────────
function playMattino(ctx, dest) {
  // C5 E5 G5 C6 E6 — peaceful sunrise arpeggio
  [[523, 0], [659, 0.32], [784, 0.64], [1047, 0.96], [1319, 1.28]].forEach(([freq, delay]) => {
    const t0   = ctx.currentTime + delay;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(1.0, t0 + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.60);
    osc.start(t0); osc.stop(t0 + 0.65);
  });
}

// ── 7. Valzer — two-note waltz swing (C5↔G5), triangle wave ───────────────────
function playValzer(ctx, dest) {
  [[523, 0, 1.0], [784, 0.38, 0.80], [523, 0.76, 0.95], [784, 1.14, 0.75]].forEach(([freq, delay, vol]) => {
    const t0   = ctx.currentTime + delay;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = 'triangle';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(vol, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.48);
    osc.start(t0); osc.stop(t0 + 0.52);
  });
}

// ── 8. Campana Grande — rich church bell with harmonics ────────────────────────
function playCampanaGrande(ctx, dest) {
  const t0 = ctx.currentTime;
  // A3 fundamental + harmonics — church bell quality
  // Scaled so the sum of peaks ≤ 1.0 at t0
  [[220, 0.60], [440, 0.25], [660, 0.10], [880, 0.05]].forEach(([freq, vol]) => {
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(vol, t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 4.0);
    osc.start(t0); osc.stop(t0 + 4.2);
  });
}

// ── 9. Marimba — iPhone-style woody arpeggio, quick decay ─────────────────────
function playMarimba(ctx, dest) {
  [[659, 0], [523, 0.13], [784, 0.26], [1047, 0.39]].forEach(([freq, delay]) => {
    const t0   = ctx.currentTime + delay;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(1.0, t0 + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
    osc.start(t0); osc.stop(t0 + 0.26);
  });
}

// ── 10. Tri-tono — classic two-note high alert ────────────────────────────────
function playTritono(ctx, dest) {
  [[1568, 0], [1047, 0.14]].forEach(([freq, delay]) => {
    const t0   = ctx.currentTime + delay;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.9, t0 + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.20);
    osc.start(t0); osc.stop(t0 + 0.24);
  });
}

// ── The gentle set (added 12-08-2026) ────────────────────────────────────────
// These are built additively: a sound is just a list of pure tones, each with
// its own entry time, loudness and fade. Everything below — chords, bells,
// arpeggios — comes from that one idea, which is also why the Android versions
// (mobile/scripts/genSounds.js) can be rendered to be identical to these.
//
// PARTIAL = [frequency Hz, start delay s, peak gain, attack s, fade-out end s]
// The fade-out end is measured from the partial's OWN start.
function playPartials(ctx, dest, type, partials) {
  partials.forEach(([freq, delay, peak, attack, decayEnd]) => {
    const t0   = ctx.currentTime + delay;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(dest);
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(peak, t0 + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + decayEnd);
    osc.start(t0); osc.stop(t0 + decayEnd + 0.05);
  });
}

// 11. Rugiada — a single drop falling: two very short high notes, the second
//     lower, so the ear hears one small "plip" rather than two beeps.
const P_RUGIADA = [[2093, 0, 0.55, 0.004, 0.18], [1568, 0.05, 0.85, 0.004, 0.30]];
// 12. Brezza — a C major chord that swells and dies away; the slow attack is
//     what makes it a breath instead of a chime.
const P_BREZZA = [[523, 0, 0.45, 0.35, 1.80], [659, 0, 0.35, 0.40, 1.80], [784, 0, 0.30, 0.45, 1.75]];
// 13. Ninnananna — three descending notes, each doubled an octave below: the
//     shadow note is what gives a lullaby its warmth.
const P_NINNANANNA = [
  [784, 0, 0.60, 0.03, 0.70], [392, 0, 0.22, 0.03, 0.70],
  [659, 0.45, 0.60, 0.03, 0.70], [330, 0.45, 0.22, 0.03, 0.70],
  [523, 0.90, 0.70, 0.03, 1.10], [262, 0.90, 0.26, 0.03, 1.10],
];
// 14. Cristallo — a real glass bell's overtones are NOT whole multiples of the
//     note (1 : 2.76 : 5.40 : 8.93), and the high ones die first. That
//     inharmonic ratio is the whole difference between "bell" and "beep".
const P_CRISTALLO = [
  [660, 0, 0.55, 0.008, 2.60], [1822, 0, 0.22, 0.008, 1.20],
  [3564, 0, 0.10, 0.006, 0.60], [5894, 0, 0.05, 0.005, 0.30],
];
// 15. Arpa — five notes of a pentatonic scale plucked upwards. A pentatonic
//     scale has no dissonant pair, so any overlap still sounds sweet.
const P_ARPA = [
  [523, 0, 0.75, 0.006, 0.55], [587, 0.075, 0.75, 0.006, 0.55],
  [659, 0.15, 0.75, 0.006, 0.55], [784, 0.225, 0.75, 0.006, 0.60],
  [880, 0.30, 0.80, 0.006, 0.75],
];
// 16. Nuvola — two notes a hair apart (440 and 442.6 Hz) beat slowly against
//     each other, the classic "chorus" shimmer, under a soft octave.
const P_NUVOLA = [[440, 0, 0.45, 0.50, 2.60], [442.6, 0, 0.45, 0.50, 2.60], [880, 0, 0.14, 0.60, 2.20]];
// 17. Onda — a low swell (root + fifth + octave) that rises like a breath.
const P_ONDA = [[220, 0, 0.55, 0.60, 2.80], [330, 0, 0.28, 0.70, 2.60], [440, 0, 0.14, 0.80, 2.20]];
// 18. Sogno — a major seventh chord spread out slowly; the seventh is what
//     makes it dreamy rather than plain.
const P_SOGNO = [
  [523, 0, 0.65, 0.02, 1.30], [659, 0.28, 0.65, 0.02, 1.30],
  [784, 0.56, 0.65, 0.02, 1.30], [988, 0.84, 0.70, 0.02, 1.60],
];
// 19. Aurora — a rising pentatonic where every note keeps its octave alight
//     above it, so the sound opens out instead of stepping up.
const P_AURORA = [
  [523, 0, 0.45, 0.02, 1.00], [1047, 0, 0.13, 0.02, 0.80],
  [659, 0.18, 0.45, 0.02, 1.00], [1319, 0.18, 0.13, 0.02, 0.80],
  [784, 0.36, 0.45, 0.02, 1.00], [1568, 0.36, 0.13, 0.02, 0.80],
  [1047, 0.54, 0.55, 0.02, 1.40], [2093, 0.54, 0.13, 0.02, 1.00],
];

// ─────────────────────────────────────────────────────────────────────────────

const SOUND_FNS = {
  rugiada:    (c, d) => playPartials(c, d, 'sine', P_RUGIADA),
  brezza:     (c, d) => playPartials(c, d, 'sine', P_BREZZA),
  ninnananna: (c, d) => playPartials(c, d, 'sine', P_NINNANANNA),
  cristallo:  (c, d) => playPartials(c, d, 'sine', P_CRISTALLO),
  arpa:       (c, d) => playPartials(c, d, 'sine', P_ARPA),
  nuvola:     (c, d) => playPartials(c, d, 'sine', P_NUVOLA),
  onda:       (c, d) => playPartials(c, d, 'sine', P_ONDA),
  sogno:      (c, d) => playPartials(c, d, 'sine', P_SOGNO),
  aurora:     (c, d) => playPartials(c, d, 'sine', P_AURORA),
  campanella:    playCampanella,
  carillon:      playCarillon,
  ping:          playPing,
  zen:           playZen,
  fanfara:       playFanfara,
  mattino:       playMattino,
  valzer:        playValzer,
  campana:       playCampanaGrande,
  marimba:       playMarimba,
  tritono:       playTritono,
};

/** Duration (seconds) of each sound — used to schedule AudioContext cleanup */
const SOUND_DURATION = {
  rugiada:    0.45,
  brezza:     2.20,
  ninnananna: 2.10,
  cristallo:  2.80,
  arpa:       1.15,
  nuvola:     3.20,
  onda:       3.10,
  sogno:      2.60,
  aurora:     2.10,
  campanella: 2.1,
  carillon:   1.4,
  ping:       0.25,
  zen:        3.6,
  fanfara:    0.7,
  mattino:    1.9,
  valzer:     1.7,
  campana:    4.2,
  marimba:    0.65,
  tritono:    0.4,
};

/**
 * Total loop period (ms) between the START of one play and the START of the next.
 * = sound duration + a short silence gap
 */
const LOOP_INTERVAL = {
  rugiada:    1600,
  brezza:     3400,
  ninnananna: 3300,
  cristallo:  4000,
  arpa:       2200,
  nuvola:     4400,
  onda:       4300,
  sogno:      3800,
  aurora:     3300,
  campanella: 3500,
  carillon:   3000,
  ping:       1500,
  zen:        6500,
  fanfara:    2500,
  mattino:    3000,
  valzer:     2800,
  campana:    5000,
  marimba:    1600,
  tritono:    1400,
};

// ── Alarm settings (stored in localStorage) ──────────────────────────────────

// SNOOZE_FOREVER: sentinel stored as maxSnooze to mean "repeat forever"
// (like a phone alarm's "Per sempre"). Any value >= 99 is treated as unlimited.
export const SNOOZE_FOREVER = 999;

export const ALARM_KEYS = {
  volume:    'alarm_volume',     // string '0.0'–'1.0', default '0.7'
  snoozeMin: 'alarm_snooze_min', // string '5'–'30',    default '5'
  maxSnooze: 'alarm_max_snooze', // '3' | '5' | '999' (forever), default '3'
  sound:     'alarm_sound',      // sound name, default 'campanella'
};

/** Read alarm settings from localStorage with sensible defaults. */
export function getAlarmSettings() {
  return {
    volume:    Math.max(0, Math.min(1, parseFloat(localStorage.getItem(ALARM_KEYS.volume)    ?? '0.7'))),
    snoozeMin: Math.max(1, Math.min(30,  parseInt(localStorage.getItem(ALARM_KEYS.snoozeMin) ?? '5',  10))),
    maxSnooze: Math.max(1, Math.min(999, parseInt(localStorage.getItem(ALARM_KEYS.maxSnooze) ?? '3',  10))),
    sound:     localStorage.getItem(ALARM_KEYS.sound) || 'campanella',
  };
}

// ── Default notification (reminder) settings (stored in localStorage) ─────────
// Separate from the alarm: this controls whether a NEW event gets a default
// reminder of type "message" (a quiet notification) and how long before.
export const NOTIF_KEYS = {
  enabled:    'notif_enabled',     // '1' | '0', default '1' (ON)
  defaultMin: 'notif_default_min', // minutes before the event, default '10'
  // 'alarm' | 'message'. A new reminder RINGS unless told otherwise.
  // It used to default to 'message' — a silent notification — so somebody who
  // set a reminder and expected to be woken heard nothing, unless they happened
  // to find the little bell in the form. For an app that promises "an alarm
  // that keeps ringing until you stop it", silence was the wrong way round.
  defaultType: 'notif_default_type',
};

/** Read default-notification settings from localStorage (ON by default). */
export function getNotifSettings() {
  const raw = localStorage.getItem(NOTIF_KEYS.enabled);
  // A corrupted stored value would make parseInt return NaN, and NaN survives
  // Math.max — the default reminder then breaks (server rejects it). Guard it.
  const parsed = parseInt(localStorage.getItem(NOTIF_KEYS.defaultMin) ?? '10', 10);
  return {
    enabled:    raw == null ? true : raw === '1',
    defaultMin: Number.isFinite(parsed) ? Math.max(0, parsed) : 10,
    // Anything other than an explicit 'message' means it rings.
    defaultType: localStorage.getItem(NOTIF_KEYS.defaultType) === 'message' ? 'message' : 'alarm',
  };
}

// ── Internal helpers ──────────────────────────────────────────────────────────

// ONE persistent AudioContext, reused for every sound. A brand-new context
// created from a timer (no user gesture) is blocked by the autoplay policy —
// but a context that was already UNLOCKED during an earlier tap keeps working,
// so an alarm fired later from a timer can still be heard.
let sharedCtx = null;
function _getCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!sharedCtx || sharedCtx.state === 'closed') sharedCtx = new AC();
  return sharedCtx;
}

/** Unlock/resume the shared audio context — call from a user gesture. */
export function unlockAudio() {
  const ctx = _getCtx();
  if (ctx && ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
}

// Auto-unlock on the first user interaction anywhere in the app, so later
// programmatic alarms can play without their own gesture.
if (typeof window !== 'undefined' && !window.__memoAudioUnlockBound) {
  window.__memoAudioUnlockBound = true;
  const unlock = () => unlockAudio();
  ['pointerdown', 'touchstart', 'mousedown', 'keydown'].forEach((ev) =>
    window.addEventListener(ev, unlock, { passive: true }));
}

function _createCtxAndPlay(name, volume) {
  try {
    const ctx = _getCtx();
    if (!ctx) return;
    const play = () => {
      const master = ctx.createGain();
      master.gain.value = Math.max(0, Math.min(1, volume));
      master.connect(ctx.destination);
      (SOUND_FNS[name] || SOUND_FNS.campanella)(ctx, master);
      const dur = SOUND_DURATION[name] || 4;
      // Free the gain node afterwards but KEEP the context alive/unlocked.
      setTimeout(() => { try { master.disconnect(); } catch (_) {} }, (dur + 0.8) * 1000);
    };
    if (ctx.state === 'suspended' && ctx.resume) {
      ctx.resume().then(play).catch(play);
    } else {
      play();
    }
  } catch (_) {
    // Web Audio unavailable — silent fail
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Play one of the alert sounds ONCE (used for preview in EventForm).
 * @param {string} name   — sound name (default 'campanella')
 * @param {number} volume — 0.0–1.0 (default 0.7)
 */
export function playAlertSound(name = 'campanella', volume = 0.7) {
  _createCtxAndPlay(name, volume);
}

/**
 * Loop an alert sound until stop() is called.
 * Used by useAlertScheduler for actual alarms.
 *
 * @param {string} name   — sound name
 * @param {number} volume — 0.0–1.0
 * @returns {{ stop: () => void }}
 */
export function loopAlertSound(name = 'campanella', volume = 0.7) {
  let stopped  = false;
  let timerId  = null;
  const period = LOOP_INTERVAL[name] || 3000;

  function fire() {
    if (stopped) return;
    _createCtxAndPlay(name, volume);
    timerId = setTimeout(fire, period);
  }

  fire();

  return {
    stop() {
      stopped = true;
      if (timerId != null) clearTimeout(timerId);
    },
  };
}

/** The 8 available alert sounds with display metadata */
export const ALERT_SOUNDS = [
  // The gentle set first: these are the ones most people want to be woken by.
  { value: 'rugiada',    emoji: '💧', name: 'Rugiada'        },
  { value: 'brezza',     emoji: '🍃', name: 'Brezza'         },
  { value: 'ninnananna', emoji: '🌙', name: 'Ninnananna'     },
  { value: 'cristallo',  emoji: '💎', name: 'Cristallo'      },
  { value: 'arpa',       emoji: '🪕', name: 'Arpa'           },
  { value: 'nuvola',     emoji: '☁️', name: 'Nuvola'         },
  { value: 'onda',       emoji: '🌊', name: 'Onda'           },
  { value: 'sogno',      emoji: '✨', name: 'Sogno'          },
  { value: 'aurora',     emoji: '🌄', name: 'Aurora'         },
  { value: 'campanella', emoji: '🔔', name: 'Campanella'     },
  { value: 'carillon',   emoji: '🎵', name: 'Carillon'       },
  { value: 'ping',       emoji: '📟', name: 'Ping'           },
  { value: 'zen',        emoji: '✶',  name: 'Zen'            },
  { value: 'fanfara',    emoji: '🎺', name: 'Fanfara'        },
  { value: 'mattino',    emoji: '🌅', name: 'Mattino'        },
  { value: 'valzer',     emoji: '💃', name: 'Valzer'         },
  { value: 'campana',    emoji: '🔕', name: 'Campana Grande' },
  { value: 'marimba',    emoji: '🎶', name: 'Marimba'        },
  { value: 'tritono',    emoji: '📱', name: 'Tri-tono'       },
];
